import dayjs, { Dayjs } from "dayjs";
import { ChipProps } from "@mui/material/Chip";
import {
  SaleDepositEditRequestStatus,
  SaleDepositEditRequestStatusFilter,
} from "../../types/sale-deposit-edit-request.type";
import { SaleCenterClosingStatus } from "../../types/sale-center-closing.type";

/** The deadline is written in the same shape as the report's own วันที่ column. */
const DEADLINE_DATE_FORMAT = "DD/MM/YYYY";

/**
 * How long the branch may still ask for a closed deposit to be reopened, counted from the
 * instant head-office accounting supervision CLOSED the branch-day — not from the business
 * date. A day closed late is therefore still disputable for the full seven days.
 */
export const EDIT_REQUEST_WINDOW_DAYS = 7;

export const EDIT_REQUEST_STATUS_LABEL: Record<
  SaleDepositEditRequestStatus,
  string
> = {
  pending: "รออนุมัติ",
  approved: "อนุมัติแล้ว",
  rejected: "ปฏิเสธแล้ว",
};

export const EDIT_REQUEST_STATUS_CHIP_COLOR: Record<
  SaleDepositEditRequestStatus,
  ChipProps["color"]
> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

/** The สถานะ filter's options, unfiltered first like every other filter in this report. */
export const EDIT_REQUEST_STATUS_FILTER: {
  label: string;
  value: SaleDepositEditRequestStatusFilter;
}[] = [
  { label: "ทั้งหมด", value: "all" },
  { label: EDIT_REQUEST_STATUS_LABEL.pending, value: "pending" },
  { label: EDIT_REQUEST_STATUS_LABEL.approved, value: "approved" },
  { label: EDIT_REQUEST_STATUS_LABEL.rejected, value: "rejected" },
];

/** Keeps the select's plain `string` value out of the typed handlers without a cast. */
export const isEditRequestStatusFilter = (
  value: string,
): value is SaleDepositEditRequestStatusFilter =>
  EDIT_REQUEST_STATUS_FILTER.some((option) => option.value === value);

/**
 * The moment the window shuts: seven days from the closing stamp.
 *
 * Deliberately a plain instant-to-instant comparison and never a calendar-day one — the
 * PWA's reprint-grant expiry compares an expiry timestamp's DATE against today's, which
 * kills any grant issued late in the evening. A request raised at 22:00 on the seventh day
 * is inside the window, and this is what says so.
 */
export const editRequestDeadline = (closedAt: string): Dayjs =>
  dayjs(closedAt).add(EDIT_REQUEST_WINDOW_DAYS, "day");

interface EditRequestOfferInput {
  /**
   * Where the deposit's branch-day stands, or `null` when no single branch-day is in view
   * and the closing summary was therefore never read.
   */
  closingStatus: SaleCenterClosingStatus | null;
  /** ISO instant the branch-day was closed; the window is counted from it. */
  closedAt: string | null;
  /** Whether this deposit already has a request waiting on the supervisor. */
  hasPendingRequest: boolean;
}

/** The ขอแก้ไข item as the row should carry it. */
export interface EditRequestOffer {
  /** Whether the request can actually be raised from this row, i.e. the item is live. */
  canRequest: boolean;
  /**
   * Why the item is dead, in Thai. Null while it is live, and null on the one refusal that
   * is deliberately left unexplained — see below.
   */
  blockedReason: string | null;
}

/** The branch-day the branch may still edit itself: dead, and deliberately silent about it. */
const UNEXPLAINED_REFUSAL: EditRequestOffer = {
  canRequest: false,
  blockedReason: null,
};

/**
 * What the row's ขอแก้ไข menu item should be: live, or dead with the reason to print under it.
 *
 * Every row keeps the item, and the menu holding it. A refusal is normally spelled out —
 * "there is no button" is indistinguishable from a bug, and each refusal below is something
 * the branch can act on: pick one day, wait for the answer, or accept that the row is final.
 *
 * The one refusal NOT worth spelling out is the branch-day the branch may still edit itself.
 * "You cannot ask to edit this, because you can edit it" tells the reader nothing they are
 * not already doing, so that item is greyed with no wording at all rather than explaining
 * the reader's own situation back to them.
 *
 * The remaining checks are ordered by what the reader most needs to hear: an outstanding
 * request means the answer is already on its way, so a request left pending past the deadline
 * still reads as pending rather than as expired.
 */
export const editRequestOffer = ({
  closingStatus,
  closedAt,
  hasPendingRequest,
}: EditRequestOfferInput): EditRequestOffer => {
  // No branch-day in view, so no closing state was ever read. The row may well be frozen —
  // saying "you can still edit this" would be a guess, and the wrong one half the time.
  if (!closingStatus) {
    return {
      canRequest: false,
      blockedReason:
        "เลือกสาขาและวันที่ให้เหลือวันเดียว เพื่อดูสถานะปิดศูนย์เซลของรายการนี้",
    };
  }

  if (closingStatus !== "closed") return UNEXPLAINED_REFUSAL;

  if (hasPendingRequest) {
    return {
      canRequest: false,
      blockedReason: "มีคำขอแก้ไขรายการนี้รออนุมัติอยู่แล้ว",
    };
  }

  // A closed branch-day always carries its closing stamp. Without one the seven days cannot
  // be counted, and guessing the deadline on a frozen row is worse than saying so.
  if (!closedAt) {
    return {
      canRequest: false,
      blockedReason: "ไม่พบวันเวลาที่ปิดศูนย์เซล จึงคำนวณระยะเวลาขอแก้ไขไม่ได้",
    };
  }

  const deadline = editRequestDeadline(closedAt);
  if (!dayjs().isBefore(deadline)) {
    return {
      canRequest: false,
      blockedReason: `พ้นกำหนดขอแก้ไขแล้ว (ภายใน ${EDIT_REQUEST_WINDOW_DAYS} วันหลังปิดศูนย์เซล ถึง ${deadline.format(
        DEADLINE_DATE_FORMAT,
      )})`,
    };
  }

  return { canRequest: true, blockedReason: null };
};
